import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ChevronDown, ChevronUp, Wallet, Coins, TrendingUp, Calendar, Shield } from 'lucide-react'
import { useConfig } from '../contexts/ConfigContext'

interface FAQItem {
  question: string
  answer: JSX.Element
  category: string
}

// Helper to render HTML safely from translations
const renderHTML = (html: string) => {
  return <span dangerouslySetInnerHTML={{ __html: html }} />
}

export default function FAQPage() {
  const { t } = useTranslation(['faq', 'common'])
  const [openIndex, setOpenIndex] = useState<number | null>(0)
  const config = useConfig()
  const activationHours = config.vault.activationDelayHours
  const miningThreshold = config.vault.miningThreshold
  const miningThresholdK = Math.round(miningThreshold / 1000)

  const buildFAQs = (): FAQItem[] => [
    {
      category: t('faq:categories.gettingStarted'),
      question: t('faq:items.faq1.question'),
      answer: (
        <div className="space-y-2">
          <p>{t('faq:items.faq1.answer.intro')}</p>
          <ul className="list-disc pl-5 space-y-1">
            <li>{renderHTML(t('faq:items.faq1.answer.list.item1'))}</li>
            <li>{renderHTML(t('faq:items.faq1.answer.list.item2'))}</li>
            <li>{renderHTML(t('faq:items.faq1.answer.list.item3'))}</li>
            <li>{renderHTML(t('faq:items.faq1.answer.list.item4'))}</li>
            <li>{renderHTML(t('faq:items.faq1.answer.list.item5'))}</li>
          </ul>
        </div>
      )
    },
    {
      category: t('faq:categories.gettingStarted'),
      question: t('faq:items.faq2.question'),
      answer: (
        <div className="space-y-2">
          <p>{renderHTML(t('faq:items.faq2.answer.intro'))}</p>
          <div className="bg-purple-500/10 border border-purple-500/30 rounded-lg p-3">
            <p className="font-semibold text-purple-400 mb-1">{t('faq:items.faq2.answer.boxTitle')}</p>
            <p className="text-sm">{t('faq:items.faq2.answer.boxDesc')}</p>
            <p className="text-xs text-gray-400 mt-1">{t('faq:items.faq2.answer.boxNote')}</p>
          </div>
        </div>
      )
    },
    {
      category: t('faq:categories.stakingProcess'),
      question: t('faq:items.faq3.question'),
      answer: (
        <div className="space-y-3">
          <p className="font-semibold text-gold-500">{t('faq:items.faq3.answer.title')}</p>

          <div className="space-y-3">
            <div className="bg-background-elevated rounded-lg p-4">
              <div className="flex gap-3">
                <div className="flex-shrink-0 w-8 h-8 bg-gold-500 text-black rounded-full flex items-center justify-center font-bold">1</div>
                <div>
                  <p className="font-semibold text-white mb-1">{t('faq:items.faq3.answer.step1Title')}</p>
                  <p className="text-sm text-gray-400">{t('faq:items.faq3.answer.step1Desc')}</p>
                </div>
              </div>
            </div>

            <div className="bg-background-elevated rounded-lg p-4">
              <div className="flex gap-3">
                <div className="flex-shrink-0 w-8 h-8 bg-gold-500 text-black rounded-full flex items-center justify-center font-bold">2</div>
                <div>
                  <p className="font-semibold text-white mb-1">{t('faq:items.faq3.answer.step2Title')}</p>
                  <p className="text-sm text-gray-400 mt-1">{t('faq:items.faq3.answer.step2Desc')}</p>
                </div>
              </div>
            </div>

            <div className="bg-background-elevated rounded-lg p-4">
              <div className="flex gap-3">
                <div className="flex-shrink-0 w-8 h-8 bg-gold-500 text-black rounded-full flex items-center justify-center font-bold">3</div>
                <div>
                  <p className="font-semibold text-white mb-1">{t('faq:items.faq3.answer.step3Title')}</p>
                  <p className="text-sm text-gray-400">{t('faq:items.faq3.answer.step3Desc')}</p>
                  <p className="text-sm text-green-400 mt-1">{t('faq:items.faq3.answer.step3Note')}</p>
                </div>
              </div>
            </div>

            <div className="bg-background-elevated rounded-lg p-4">
              <div className="flex gap-3">
                <div className="flex-shrink-0 w-8 h-8 bg-laika-purple text-black rounded-full flex items-center justify-center font-bold">4</div>
                <div>
                  <p className="font-semibold text-white mb-1">{t('faq:items.faq3.answer.step4Title')}</p>
                  <p className="text-sm text-gray-400">{t('faq:items.faq3.answer.step4Desc')}</p>
                  <p className="text-sm text-green-400 mt-1">{t('faq:items.faq3.answer.step4Note')}</p>
                </div>
              </div>
            </div>

            <div className="bg-background-elevated rounded-lg p-4">
              <div className="flex gap-3">
                <div className="flex-shrink-0 w-8 h-8 bg-gold-500 text-black rounded-full flex items-center justify-center font-bold">5</div>
                <div>
                  <p className="font-semibold text-white mb-1">{t('faq:items.faq3.answer.step5Title')}</p>
                  <p className="text-sm text-gray-400">{t('faq:items.faq3.answer.step5Desc')}</p>
                </div>
              </div>
            </div>

            <div className="bg-background-elevated rounded-lg p-4">
              <div className="flex gap-3">
                <div className="flex-shrink-0 w-8 h-8 bg-green-500 text-black rounded-full flex items-center justify-center font-bold">6</div>
                <div>
                  <p className="font-semibold text-white mb-1">{t('faq:items.faq3.answer.step6Title')}</p>
                  <p className="text-sm text-gray-400">{t('faq:items.faq3.answer.step6Desc')}</p>
                  <p className="text-sm text-gray-400 mt-1">{t('faq:items.faq3.answer.step6Note', { activationHours })}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )
    },
    {
      category: t('faq:categories.stakingProcess'),
      question: t('faq:items.faq4.question'),
      answer: (
        <div className="space-y-3">
          <p>{renderHTML(t('faq:items.faq4.answer.intro'))}</p>

          <div className="bg-purple-500/10 border border-purple-500/30 rounded-lg p-4">
            <p className="font-semibold text-purple-400 mb-2">{t('faq:items.faq4.answer.boxTitle')}</p>
            <p className="text-sm text-gray-300">{t('faq:items.faq4.answer.boxDesc')}</p>
            <p className="text-sm text-gray-300 mt-1">{t('faq:items.faq4.answer.boxNote')}</p>
          </div>

          <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-3 mt-3">
            <p className="text-sm text-green-400">
              {renderHTML(t('faq:items.faq4.answer.successText'))}
            </p>
          </div>
        </div>
      )
    },
    {
      category: t('faq:categories.laikaBoost'),
      question: t('faq:items.faq5.question'),
      answer: (
        <div className="space-y-2">
          <p>{renderHTML(t('faq:items.faq5.answer.intro'))}</p>
          <p className="text-gray-400">{t('faq:items.faq5.answer.exampleLabel')}</p>
          <ul className="list-disc pl-5 space-y-1 text-gray-300">
            <li>{t('faq:items.faq5.answer.example1')}</li>
            <li>{t('faq:items.faq5.answer.example2')}</li>
            <li>{t('faq:items.faq5.answer.example3')}</li>
          </ul>
          <div className="bg-teal-500/10 border border-teal-500/30 rounded-lg p-3 mt-3">
            <p className="text-sm text-teal-400">
              {t('faq:items.faq5.answer.note')}
            </p>
          </div>
        </div>
      )
    },
    {
      category: t('faq:categories.vaultActivation'),
      question: t('faq:items.faq6.question'),
      answer: (
        <div className="space-y-3">
          <p>{t('faq:items.faq6.answer.intro')}</p>

          <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-4">
            <p className="font-semibold text-yellow-400 mb-2">{t('faq:items.faq6.answer.phase1Title')}</p>
            <p className="text-sm text-gray-300">{renderHTML(t('faq:items.faq6.answer.phase1Desc', { miningThreshold: miningThreshold.toLocaleString() }))}</p>
            <p className="text-sm text-gray-400 mt-1">{t('faq:items.faq6.answer.phase1Note')}</p>
          </div>

          <div className="bg-teal-500/10 border border-teal-500/30 rounded-lg p-4">
            <p className="font-semibold text-teal-400 mb-2">{t('faq:items.faq6.answer.phase2Title', { activationHours })}</p>
            <p className="text-sm text-gray-300">{t('faq:items.faq6.answer.phase2Desc', { miningThresholdK, activationHours })}</p>
            <p className="text-sm text-gray-400 mt-1">{t('faq:items.faq6.answer.phase2Note')}</p>
          </div>

          <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-4">
            <p className="font-semibold text-green-400 mb-2">{t('faq:items.faq6.answer.activeTitle', { activationHours })}</p>
            <p className="text-sm text-gray-300 mt-1">{t('faq:items.faq6.answer.activeDesc')}</p>
          </div>
        </div>
      )
    },
    {
      category: t('faq:categories.earningsWithdrawals'),
      question: t('faq:items.faq7.question'),
      answer: (
        <div className="space-y-3">
          <p>{t('faq:items.faq7.answer.intro')}</p>

          <div className="bg-background-elevated rounded-lg p-4 space-y-3">
            <div>
              <p className="font-semibold text-gold-400 mb-1">{t('faq:items.faq7.answer.monthlyTitle')}</p>
              <p className="text-sm text-gray-300">{t('faq:items.faq7.answer.monthlyDesc')}</p>
            </div>
          </div>

          <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-3">
            <p className="text-sm text-green-400">
              {t('faq:items.faq7.answer.note')}
            </p>
          </div>
        </div>
      )
    },
    {
      category: t('faq:categories.wexelMarketplace'),
      question: t('faq:items.faq8.question'),
      answer: (
        <div className="space-y-3">
          <p>{renderHTML(t('faq:items.faq8.answer.intro'))}</p>

          <div className="bg-background-elevated rounded-lg p-4 space-y-2">
            <p className="font-semibold text-white mb-2">{t('faq:items.faq8.answer.containsTitle')}</p>
            <ul className="text-sm text-gray-300 space-y-1">
              <li>{t('faq:items.faq8.answer.contains1')}</li>
              <li>{t('faq:items.faq8.answer.contains2')}</li>
              <li>{t('faq:items.faq8.answer.contains3')}</li>
              <li>{t('faq:items.faq8.answer.contains4')}</li>
              <li>{t('faq:items.faq8.answer.contains5')}</li>
              <li>{t('faq:items.faq8.answer.contains6')}</li>
            </ul>
          </div>

          <div className="bg-purple-500/10 border border-purple-500/30 rounded-lg p-4">
            <p className="font-semibold text-purple-400 mb-2">{t('faq:items.faq8.answer.benefitsTitle')}</p>
            <ul className="text-sm text-gray-300 space-y-1">
              <li>{renderHTML(t('faq:items.faq8.answer.benefits1'))}</li>
              <li>{renderHTML(t('faq:items.faq8.answer.benefits2'))}</li>
              <li>{renderHTML(t('faq:items.faq8.answer.benefits3'))}</li>
              <li>{renderHTML(t('faq:items.faq8.answer.benefits4'))}</li>
            </ul>
          </div>

          <div className="bg-teal-500/10 border border-teal-500/30 rounded-lg p-4">
            <p className="font-semibold text-teal-400 mb-2">{t('faq:items.faq8.answer.findTitle')}</p>
            <p className="text-sm text-gray-300">{t('faq:items.faq8.answer.findDesc')}</p>
            <p className="text-sm text-gray-400 mt-1">{t('faq:items.faq8.answer.findNote')}</p>
          </div>
        </div>
      )
    },
    {
      category: t('faq:categories.wexelMarketplace'),
      question: t('faq:items.faq9.question'),
      answer: (
        <div className="space-y-3">
          <div className="bg-purple-500/10 border border-purple-500/30 rounded-lg p-4">
            <p className="font-semibold text-purple-400 mb-2">{t('faq:items.faq9.answer.sellTitle')}</p>
            <p className="text-sm text-gray-300 mb-2">{t('faq:items.faq9.answer.sellIntro')}</p>
            <ul className="text-sm text-gray-300 space-y-1">
              <li>{t('faq:items.faq9.answer.sell1')}</li>
              <li>{t('faq:items.faq9.answer.sell2')}</li>
              <li>{t('faq:items.faq9.answer.sell3')}</li>
              <li>{t('faq:items.faq9.answer.sell4')}</li>
            </ul>
          </div>

          <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-4">
            <p className="font-semibold text-green-400 mb-2">{t('faq:items.faq9.answer.buyTitle')}</p>
            <p className="text-sm text-gray-300 mb-2">{t('faq:items.faq9.answer.buyIntro')}</p>
            <ul className="text-sm text-gray-300 space-y-1">
              <li>{t('faq:items.faq9.answer.buy1')}</li>
              <li>{t('faq:items.faq9.answer.buy2')}</li>
              <li>{t('faq:items.faq9.answer.buy3')}</li>
              <li>{t('faq:items.faq9.answer.buy4')}</li>
            </ul>
          </div>

          <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-3">
            <p className="text-sm text-yellow-400">
              {renderHTML(t('faq:items.faq9.answer.whyNote'))}
            </p>
          </div>
        </div>
      )
    },
    {
      category: t('faq:categories.securitySafety'),
      question: t('faq:items.faq10.question'),
      answer: (
        <div className="space-y-3">
          <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-4">
            <p className="font-semibold text-green-400 mb-2">{t('faq:items.faq10.answer.box1Title')}</p>
            <p className="text-sm text-gray-300">{t('faq:items.faq10.answer.box1Desc')}</p>
            <p className="text-sm text-gray-400">{t('faq:items.faq10.answer.box1Note')}</p>
          </div>

          <div className="bg-teal-500/10 border border-teal-500/30 rounded-lg p-4">
            <p className="font-semibold text-teal-400 mb-2">{t('faq:items.faq10.answer.box2Title')}</p>
            <p className="text-sm text-gray-300">{t('faq:items.faq10.answer.box2Desc')}</p>
            <p className="text-sm text-gray-400">{t('faq:items.faq10.answer.box2Note')}</p>
          </div>

          <div className="bg-purple-500/10 border border-purple-500/30 rounded-lg p-4">
            <p className="font-semibold text-purple-400 mb-2">{t('faq:items.faq10.answer.box3Title')}</p>
            <p className="text-sm text-gray-300">{t('faq:items.faq10.answer.box3Desc')}</p>
            <p className="text-sm text-gray-400">{t('faq:items.faq10.answer.box3Note')}</p>
          </div>

          <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-3 mt-3">
            <p className="text-sm text-yellow-400">
              {renderHTML(t('faq:items.faq10.answer.warning'))}
            </p>
          </div>
        </div>
      )
    }
  ]

  const faqs = buildFAQs()

  const categories = Array.from(new Set(faqs.map(faq => faq.category)))

  return (
    <div className="min-h-screen py-12">
      <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl sm:text-5xl font-bold text-white mb-4">
            {t('faq:title')}
          </h1>
          <p className="text-lg text-gray-400 max-w-2xl mx-auto">
            {t('faq:subtitle')}
          </p>
        </div>

        {/* Quick Links */}
        <div className="bg-gradient-to-br from-gold-500/10 to-transparent border border-gold-500/30 rounded-xl p-6 mb-12">
          <p className="text-sm text-gold-500 font-semibold mb-3">{t('faq:quickNavigation')}</p>
          <div className="flex flex-wrap gap-2">
            {categories.map((category) => (
              <button
                key={category}
                onClick={() => {
                  const index = faqs.findIndex(faq => faq.category === category)
                  setOpenIndex(index)
                  document.getElementById(`faq-${index}`)?.scrollIntoView({ behavior: 'smooth', block: 'center' })
                }}
                className="px-4 py-2 bg-background-elevated hover:bg-gold-500/20 border border-gold-500/30 rounded-lg text-sm text-gray-300 hover:text-gold-400 transition-colors"
              >
                {category}
              </button>
            ))}
          </div>
        </div>

        {/* FAQ Items */}
        <div className="space-y-4">
          {faqs.map((faq, index) => {
            const isOpen = openIndex === index
            const isFirstInCategory = index === 0 || faqs[index - 1].category !== faq.category

            return (
              <div key={index} id={`faq-${index}`}>
                {isFirstInCategory && (
                  <h2 className="text-2xl font-bold text-gold-500 mt-8 mb-4 flex items-center gap-2">
                    {faq.category === t('faq:categories.gettingStarted') && <Wallet className="h-6 w-6" />}
                    {faq.category === t('faq:categories.stakingProcess') && <Coins className="h-6 w-6" />}
                    {faq.category === t('faq:categories.laikaBoost') && <span className="text-2xl">🚀</span>}
                    {faq.category === t('faq:categories.vaultActivation') && <Calendar className="h-6 w-6" />}
                    {faq.category === t('faq:categories.earningsWithdrawals') && <span className="text-2xl">💰</span>}
                    {faq.category === t('faq:categories.wexelMarketplace') && <span className="text-2xl">🎫</span>}
                    {faq.category === t('faq:categories.securitySafety') && <Shield className="h-6 w-6" />}
                    {faq.category}
                  </h2>
                )}

                <div className="bg-background-card rounded-xl border border-green-900/20 overflow-hidden hover:border-gold-500/30 transition-colors">
                  <button
                    onClick={() => setOpenIndex(isOpen ? null : index)}
                    className="w-full px-6 py-4 flex items-center justify-between text-left"
                  >
                    <span className="font-semibold text-white pr-4">{faq.question}</span>
                    {isOpen ? (
                      <ChevronUp className="h-5 w-5 text-gold-500 flex-shrink-0" />
                    ) : (
                      <ChevronDown className="h-5 w-5 text-gray-400 flex-shrink-0" />
                    )}
                  </button>

                  {isOpen && (
                    <div className="px-6 pb-4 text-gray-300">
                      {typeof faq.answer === 'string' ? (
                        <p>{faq.answer}</p>
                      ) : (
                        faq.answer
                      )}
                    </div>
                  )}
                </div>
              </div>
            )
          })}
        </div>

        {/* Still Have Questions */}
        <div className="mt-16 bg-gradient-to-br from-green-900/20 to-transparent rounded-xl p-8 text-center">
          <h3 className="text-2xl font-bold text-white mb-4">{t('faq:stillHaveQuestions.title')}</h3>
          <p className="text-gray-400 mb-6">
            {t('faq:stillHaveQuestions.subtitle')}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="#"
              className="btn-gold inline-flex items-center justify-center gap-2 px-6 py-3 rounded-lg font-semibold"
            >
              {t('faq:stillHaveQuestions.joinCommunity')}
            </a>
            <a
              href="/vaults"
              className="btn-outline-gold inline-flex items-center justify-center gap-2 px-6 py-3 rounded-lg font-semibold"
            >
              {t('faq:stillHaveQuestions.startStaking')}
            </a>
          </div>
        </div>
      </div>
    </div>
  )
}
